import React, { useState, useEffect } from 'react';
import { FaPlus, FaExclamationCircle } from 'react-icons/fa';
import IssueDetailsPanel from './IssueDetailsPanel';
import { apiFetch } from '../../../utils/apiAdmin';
import { apiFetch as clientApiFetch } from '../../../utils/apiClient';

const IssuesTable = ({
    issues,
    members,
    tasks,
    onIssueClick,
    onRefresh,
    leadId,
    isAdmin = false,
    preventRefreshRef,
    currentUserEmail,
    hideHeader = false,
    showCreate = false,
    onShowCreateChange
}) => {
    const [selectedIssue, setSelectedIssue] = useState(null);
    const [showCreatePanel, setShowCreatePanel] = useState(false);

    // Handle controlled create mode from parent
    useEffect(() => {
        if (showCreate) {
            setShowCreatePanel(true);
        }
    }, [showCreate]);

    const handleIssueClick = (issue) => {
        setSelectedIssue(issue);
        if (onIssueClick) onIssueClick(issue);
    };

    const handleClosePanel = () => {
        setSelectedIssue(null);
        setShowCreatePanel(false);
        if (onShowCreateChange) onShowCreateChange(false);
    };

    // The assignee submits only { status, note }; everything else is admin-only
    const handleClientSave = async (issueData) => {
        if (!selectedIssue) return;
        if (selectedIssue.assignedToEmail !== currentUserEmail) return;

        const baseUrl = process.env.REACT_APP_API_URL;

        // Prevent WebSocket refresh during our own update
        if (preventRefreshRef) preventRefreshRef.current = true;

        try {
            const response = await clientApiFetch(
                `${baseUrl}/client/issues/${selectedIssue.issueId}`,
                {
                    method: 'PUT',
                    body: JSON.stringify({
                        status: issueData.status,
                        note: issueData.note
                    })
                }
            );
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.error || 'Failed to update issue');
            }
            const result = await response.json();
            // Keep panel open, update selected issue with saved data
            setSelectedIssue(result.issue);

            // Silent refresh to update table
            if (onRefresh) onRefresh(false);
        } catch (err) {
            console.error('Error saving issue:', err);
            alert('Failed to update issue: ' + err.message);
            // Rethrow so the panel knows the save failed and keeps the typed
            // note rather than clearing it
            throw err;
        } finally {
            // Re-enable WebSocket refresh after a delay (allow notification to pass)
            setTimeout(() => {
                if (preventRefreshRef) preventRefreshRef.current = false;
            }, 2000);
        }
    };

    const handleSave = async (issueData) => {
        if (!isAdmin) return handleClientSave(issueData);

        const baseUrl = process.env.REACT_APP_API_URL;

        try {
            if (selectedIssue) {
                // Update existing issue
                const response = await apiFetch(
                    `${baseUrl}/admin/issues/${leadId}/${selectedIssue.issueId}`,
                    {
                        method: 'PUT',
                        body: JSON.stringify(issueData)
                    }
                );
                if (!response.ok) throw new Error('Failed to update issue');
                const result = await response.json();
                // Keep panel open, update selected issue with saved data
                setSelectedIssue(result.issue);
            } else {
                // Create new issue
                const response = await apiFetch(
                    `${baseUrl}/admin/issues/${leadId}`,
                    {
                        method: 'POST',
                        body: JSON.stringify(issueData)
                    }
                );
                if (!response.ok) throw new Error('Failed to create issue');
                // Close panel after creating new issue
                handleClosePanel();
            }

            // Silent refresh to update table
            if (onRefresh) onRefresh(false);
        } catch (err) {
            console.error('Error saving issue:', err);
            alert('Failed to save issue: ' + err.message);
        }
    };

    // Approve or reject a client's resolution. Separate endpoints from the
    // ordinary PUT so that only a real decision emails the client.
    const handleDecide = async (decision, comment) => {
        if (!isAdmin || !selectedIssue) return;

        const baseUrl = process.env.REACT_APP_API_URL;

        if (preventRefreshRef) preventRefreshRef.current = true;

        try {
            const response = await apiFetch(
                `${baseUrl}/admin/issues/${leadId}/${selectedIssue.issueId}/${decision}`,
                {
                    method: 'POST',
                    body: JSON.stringify({ comment })
                }
            );
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.error || `Failed to ${decision} issue`);
            }
            const result = await response.json();
            setSelectedIssue(result.issue);

            if (onRefresh) onRefresh(false);
        } catch (err) {
            console.error(`Error on ${decision}:`, err);
            alert(`Failed to ${decision} issue: ` + err.message);
            // Rethrow so the panel keeps the typed comment
            throw err;
        } finally {
            setTimeout(() => {
                if (preventRefreshRef) preventRefreshRef.current = false;
            }, 2000);
        }
    };

    const handleDelete = async (issueId) => {
        if (!isAdmin) return;
        if (!window.confirm('Are you sure you want to delete this issue?')) return;

        const baseUrl = process.env.REACT_APP_API_URL;

        try {
            const response = await apiFetch(
                `${baseUrl}/admin/issues/${leadId}/${issueId}`,
                { method: 'DELETE' }
            );
            if (!response.ok) throw new Error('Failed to delete issue');

            handleClosePanel();
            // Silent refresh to update table
            if (onRefresh) onRefresh(false);
        } catch (err) {
            console.error('Error deleting issue:', err);
            alert('Failed to delete issue: ' + err.message);
        }
    };

    // Issues awaiting an admin decision float to the top so they cannot be
    // lost in a long list. Order within each group is left as it arrived.
    const sortedIssues = [...issues].sort((a, b) => {
        const aWaiting = a.status === 'Resolved by Client' ? 0 : 1;
        const bWaiting = b.status === 'Resolved by Client' ? 0 : 1;
        return aWaiting - bWaiting;
    });

    const awaitingCount = issues.filter(i => i.status === 'Resolved by Client').length;

    const getStatusBadge = (status) => {
        const statusClasses = {
            'New': 'bg-blue-100 text-blue-800',
            'Acknowledged': 'bg-amber-100 text-amber-800',
            'Need Info': 'bg-red-100 text-red-800 animate-pulse',
            'Resolved by Client': 'bg-teal-100 text-teal-800',
            'Closed': 'bg-green-100 text-green-800'
        };
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`}>
                {status}
            </span>
        );
    };

    const getPriorityBadge = (priority) => {
        const priorityClasses = {
            'High': 'bg-red-100 text-red-800',
            'Medium': 'bg-orange-100 text-orange-800',
            'Low': 'bg-blue-100 text-blue-800'
        };
        return priority ? (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${priorityClasses[priority] || 'bg-gray-100 text-gray-800'}`}>
                {priority}
            </span>
        ) : (
            <span className="text-gray-400 text-xs">-</span>
        );
    };

    return (
        <div className={`h-full flex flex-col ${hideHeader ? '' : 'bg-white rounded-lg shadow'}`}>
            {/* Header */}
            {!hideHeader && (
                <div className="flex items-center justify-between px-4 py-3 border-b">
                    <div className="flex items-center gap-2">
                        <h2 className="text-lg font-semibold">Issues</h2>
                        <span className="text-sm text-gray-500">({issues.length})</span>
                        {isAdmin && awaitingCount > 0 && (
                            <span className="px-2 py-0.5 rounded-full bg-teal-100 text-teal-800 text-xs font-semibold">
                                {awaitingCount} awaiting approval
                            </span>
                        )}
                    </div>
                    {isAdmin && (
                        <button
                            onClick={() => setShowCreatePanel(true)}
                            className="flex items-center gap-2 px-3 py-1.5 bg-primary-600 text-white rounded hover:bg-primary-700 text-sm"
                        >
                            <FaPlus size={12} />
                            Add Issue
                        </button>
                    )}
                </div>
            )}

            {/* Table */}
            <div className="flex-1 overflow-auto">
                {issues.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500">
                        <FaExclamationCircle size={48} className="text-gray-300 mb-4" />
                        <p>No issues have been logged yet.</p>
                    </div>
                ) : (
                    <table className="w-full table-fixed">
                        <thead className="bg-gray-50 sticky top-0">
                            <tr>
                                <th style={{width: '8%'}} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                                <th style={{width: '17.5%'}} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                                <th style={{width: '11%'}} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th style={{width: '9%'}} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                                <th style={{width: '11%'}} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                <th style={{width: '8.8%'}} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned To</th>
                                <th style={{width: '24.7%'}} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Blockers</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {sortedIssues.map(issue => (
                                <tr
                                    key={issue.issueId}
                                    onClick={() => handleIssueClick(issue)}
                                    className="hover:bg-gray-50 cursor-pointer"
                                >
                                    <td className="px-4 py-3 text-sm font-mono font-medium text-gray-900">
                                        {issue.issueId}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900 truncate">
                                        {issue.title}
                                    </td>
                                    <td className="px-4 py-3">
                                        {getStatusBadge(issue.status)}
                                    </td>
                                    <td className="px-4 py-3">
                                        {getPriorityBadge(issue.priority)}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600">
                                        {issue.type || '-'}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600 truncate">
                                        {issue.assignedToName || '-'}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600">
                                        {(issue.blockedTasks?.length || 0) > 0 ? (
                                            <div className="text-xs text-gray-600 truncate" title={
                                                issue.blockedTasks.map(taskId => {
                                                    const task = tasks.find(t => t.taskId?.toString() === taskId);
                                                    if (!task) return '';
                                                    const parentTask = task.mainTaskId
                                                        ? tasks.find(t => t.taskId === task.mainTaskId)
                                                        : null;
                                                    return parentTask
                                                        ? `${parentTask.subject} / ${task.subject}`
                                                        : task.subject;
                                                }).filter(Boolean).join(', ')
                                            }>
                                                {issue.blockedTasks.map((taskId, idx) => {
                                                    const task = tasks.find(t => t.taskId?.toString() === taskId);
                                                    if (!task) return null;
                                                    const parentTask = task.mainTaskId
                                                        ? tasks.find(t => t.taskId === task.mainTaskId)
                                                        : null;
                                                    const displayName = parentTask
                                                        ? `${parentTask.subject} / ${task.subject}`
                                                        : task.subject;
                                                    const closedStyle = task.isClosed ? 'text-gray-400 line-through' : '';
                                                    return (
                                                        <span key={taskId} className={closedStyle}>
                                                            {idx > 0 && ', '}
                                                            {displayName}
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        ) : '-'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Issue Details Panel */}
            {(selectedIssue || showCreatePanel) && (
                <IssueDetailsPanel
                    issue={selectedIssue}
                    members={members}
                    tasks={tasks}
                    isAdmin={isAdmin}
                    currentUserEmail={currentUserEmail}
                    onClose={handleClosePanel}
                    onSave={handleSave}
                    onDelete={handleDelete}
                    onDecide={handleDecide}
                />
            )}
        </div>
    );
};

export default IssuesTable;
